import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const ToastContext = createContext(null);

const TOAST_DURATION = 3500;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback((message, type = 'info', duration = TOAST_DURATION) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, type }]);

    setTimeout(() => {
      removeToast(id);
    }, duration);
  }, [removeToast]);

  const value = useMemo(() => ({
    success: (message, duration) => addToast(message, 'success', duration),
    error: (message, duration) => addToast(message, 'error', duration),
    info: (message, duration) => addToast(message, 'info', duration),
  }), [addToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed top-4 right-4 z-[1200] flex flex-col gap-2 pointer-events-none">
        {toasts.map((toast) => {
          const variants = {
            success: 'border-green-500/50 bg-green-500/10 text-green-100',
            error: 'border-red-500/50 bg-red-500/10 text-red-100',
            info: 'border-bronze/50 bg-bronze/10 text-light',
          };

          return (
            <div
              key={toast.id}
              className={`pointer-events-auto min-w-[260px] max-w-[360px] rounded-lg border px-4 py-3 shadow-xl backdrop-blur ${variants[toast.type] || variants.info}`}
              role="status"
              aria-live="polite"
            >
              <div className="flex items-start gap-3">
                <p className="text-sm leading-5">{toast.message}</p>
                <button
                  type="button"
                  className="ml-auto text-light-muted hover:text-light"
                  onClick={() => removeToast(toast.id)}
                  aria-label="Close notification"
                >
                  ×
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

